<script setup lang="ts">
import type { TooltipOptions } from 'bootstrap';
import $ from 'jquery';
import {
    nextTick,
    onMounted,
    onUnmounted,
    onUpdated,
    ref,
    unref,
    watch,
} from 'vue';

import { useGenerateIds } from '#public_ts/composables';

import { typeSafeObjectKeys } from '../commons/fe-commons';

const props = withDefaults(defineProps<{
    id?: string;
    element?: HTMLElement;
    animation?: boolean;
    container?: 'body' | string | false;
    delay?: TooltipOptions['delay'];
    placement?: TooltipOptions['placement'];
    title?: TooltipOptions['title'];
    trigger?: TooltipOptions['trigger'];
    template?: TooltipOptions['template'];
}>(), {
    id: undefined,
    element: undefined,
    animation: undefined,
    container: undefined,
    delay: undefined,
    placement: undefined,
    title: undefined,
    trigger: undefined,
    template: undefined,
});

const generateIds = useGenerateIds();

const controlId = ref(props.id || generateIds());
let currentElement: HTMLElement | undefined;
let initialized = false;

function updateToolTip() {
    if(currentElement) {
        destroyToolTip();
    }
    const $el = props.element ? $(props.element) : $(`#${unref(controlId)}`);
    if($el.length > 0) {
        const options = {
            animation: props.animation,
            container: props.container,
            delay: props.delay,
            placement: props.placement,
            title: props.title,
            trigger: props.trigger,
        };
        typeSafeObjectKeys(options).forEach((key) => {
            if(options[key] === undefined) {
                delete options[key];
            }
        });
        $el.tooltip(options);
        currentElement = $el[0];
    }
}

function destroyToolTip() {
    if(currentElement) {
        try {
            $(currentElement).tooltip('destroy');
        } catch(err) { /**/ }
        currentElement = undefined;
    }
}

watch([() => props.id, () => props.element], () => {
    if(!initialized) return;
    if(!props.id && !props.element) {
        controlId.value = generateIds();
    }
    nextTick(() => {
        updateToolTip();
    });
});

onMounted(() => {
    //portal requires nextTick
    nextTick(() => {
        nextTick(() => {
            initialized = true;
            updateToolTip();
        });
    });
});
onUnmounted(destroyToolTip);
onUpdated(updateToolTip);
</script>
<template>
    <slot :props="{'id': controlId, 'data-toggle': 'tooltip'}" />
</template>
